// Privacy + cookies + about pages. Plain-text, no AI, no data fetching.
// Rendered into the route host like the blog views.

export function renderPrivacy(): string {
  return `
    <article class="legal-page">
      <header class="legal-header">
        <a href="#/" class="legal-back">← Back to flows</a>
        <h1>Privacy</h1>
        <p class="legal-lede">What we store, why, and how to clear it. Last updated 2026-05-23.</p>
      </header>

      <section>
        <h2>The short version</h2>
        <p>The Frozen Fly is a free, no-account dashboard. We don't run analytics, we don't track you across the web, we don't sell anything, and we don't have ads. The only data tied to <em>you</em> lives in your browser's localStorage — never on our servers.</p>
      </section>

      <section>
        <h2>What lives in your browser</h2>
        <p>Four small keys in <strong>localStorage</strong> (under <code>thefrozenfly.com</code>), each storing one preference:</p>
        <ul>
          <li><code>frozen-fly:lenses-v1</code> — which lens chips (🚣 Whitewater, 🎣 Fly Fishing) you have active.</li>
          <li><code>frozen-fly:regions-v1</code> — which regions you have active in the region filter.</li>
          <li><code>frozen-fly:user-location-v1</code> — only set if you click the "Use my location" button and grant the browser geolocation prompt. Stored: latitude, longitude, accuracy, and capture timestamp. Used only to sort rivers by proximity. Auto-expires after 24 hours.</li>
          <li>(Reading progress for devotional articles, set by the Breathe section.)</li>
        </ul>
        <p>That's the entire list. Nothing else is stored.</p>
      </section>

      <section>
        <h2>What we DON'T do</h2>
        <ul>
          <li>No first-party or third-party analytics (Google Analytics, Plausible, etc.) — not installed.</li>
          <li>No advertising trackers, no Facebook Pixel, no marketing cookies.</li>
          <li>No account, no email collected, no IP address logged.</li>
          <li>No personal data leaves your browser — we have no server-side user profile to leave it for.</li>
        </ul>
      </section>

      <section>
        <h2>What the server <em>does</em> know</h2>
        <p>Like every website, Cloudflare's edge servers (which host the site) see request metadata — your IP, user-agent, requested URL, response time. This is standard CDN telemetry used for delivering pages and protecting against abuse, with retention controlled by Cloudflare. We don't log it ourselves, we don't query it, and we don't tie it to anything else.</p>
        <p>The AI narratives on each river card are generated server-side ahead of time (every 15 minutes via a scheduled job) and served from a cache. Your visit does not trigger or influence any AI call. No content you generate or browse on the site is sent to any AI provider.</p>
      </section>

      <section>
        <h2>Geolocation specifically</h2>
        <p>If you click "Use my location," your browser will ask for permission. If you grant it, the latitude/longitude are read by JavaScript running in your browser, stored in <code>localStorage</code>, and used only to compute distance to rivers for sorting. They are never sent to any server. To revoke: click "Clear my location" in the filter bar, or clear site data from your browser settings, or simply deny the prompt.</p>
      </section>

      <section>
        <h2>How to wipe everything</h2>
        <p>In your browser: open developer tools (⌘⌥I / Ctrl+Shift+I) → Application → Storage → Clear site data, OR use the browser's "Clear browsing data" settings and select <code>thefrozenfly.com</code>. Every preference resets to defaults.</p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>Questions or corrections: <a href="mailto:michael.jerrow@gmail.com" class="link">michael.jerrow@gmail.com</a>.</p>
      </section>
    </article>
  `;
}

export function renderCookies(): string {
  return `
    <article class="legal-page">
      <header class="legal-header">
        <a href="#/" class="legal-back">← Back to flows</a>
        <h1>Cookies & Storage</h1>
        <p class="legal-lede">The Frozen Fly uses <strong>zero traditional cookies</strong>. We do use a small amount of browser localStorage to remember your filter preferences.</p>
      </header>

      <section>
        <h2>HTTP cookies</h2>
        <p>The site sets no HTTP cookies of its own. No tracking, no analytics, no advertising, no session cookies. Cloudflare's edge may set a security/anti-bot cookie (<code>__cf_bm</code>) for fraud protection; that's controlled by Cloudflare and rotates per request — we don't use it.</p>
      </section>

      <section>
        <h2>localStorage</h2>
        <p>localStorage is browser-side key/value storage. It never leaves your device. We use it for these preferences (all auto-expire if you clear site data):</p>
        <ul>
          <li><strong>Lens filter</strong> (<code>frozen-fly:lenses-v1</code>) — your 🚣 / 🎣 selection.</li>
          <li><strong>Region filter</strong> (<code>frozen-fly:regions-v1</code>) — which of the 8 regions you have active.</li>
          <li><strong>Location</strong> (<code>frozen-fly:user-location-v1</code>) — only present if you used the geolocation feature. Auto-expires after 24 hours.</li>
          <li><strong>Reading progress</strong> — set by the Breathe section to remember where you stopped in a devotional.</li>
        </ul>
        <p>None of these are sent to any server. Together they are well under 1KB.</p>
      </section>

      <section>
        <h2>Why no consent banner?</h2>
        <p>EU/UK ePrivacy and GDPR require explicit consent for non-essential cookies and similar tracking. Our localStorage usage is "strictly necessary" / "preference" data with no cross-site tracking and no third-party recipients — it falls under the exemption from prior consent. Geolocation is opt-in by browser permission prompt, which IS the explicit consent mechanism. So no separate banner is required.</p>
        <p>If we ever add analytics or advertising, this page will change and a consent banner will appear before any tracker loads.</p>
      </section>

      <section>
        <h2>Full privacy details</h2>
        <p>See the <a href="#/privacy" class="link">privacy policy</a> for the full picture.</p>
      </section>
    </article>
  `;
}
